import {
	ChangeDetectionStrategy,
	Component,
	computed,
	inject,
	signal
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AuthService } from '@core/auth';
import { Toast } from '@core/services';
import { environment } from '../../../../environments/environment';
import { AiChatService, ChatMessage } from './ai-chat.service';

const SUGGESTIONS = [
	'Why did delayed shipments jump this week?',
	'Summarise the on-time rate trend',
	'Which carrier is underperforming?'
];

/**
 * Global AI assistant (design 1c): a dismissible "Ask AI" pill that opens a
 * docked chat panel. Mounted once at the app root so it is reachable from any
 * page. Answers come from MiniMax-M3 through the backend proxy.
 */
@Component({
	selector: 'app-ai-chat',
	standalone: true,
	imports: [FormsModule],
	templateUrl: './ai-chat.component.html',
	styleUrl: './ai-chat.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class AiChatComponent {
	#ai = inject(AiChatService);
	#auth = inject(AuthService);

	/** Shown only when the feature flag is on and a user is logged in. */
	readonly enabled = computed(
		() =>
			environment.aiChatEnabled &&
			!!this.#auth.user() &&
			this.#auth.isLoggedIn()
	);

	readonly suggestions = SUGGESTIONS;

	readonly open = signal(false);
	readonly dismissed = signal(false);
	readonly messages = signal<ChatMessage[]>([]);
	readonly typing = signal(false);
	readonly draft = signal('');

	readonly empty = computed(() => this.messages().length === 0);

	toggle(): void {
		this.open.update((v) => !v);
	}

	dismiss(event: Event): void {
		event.stopPropagation();
		this.dismissed.set(true);
		this.open.set(false);
	}

	restore(): void {
		this.dismissed.set(false);
	}

	clear(): void {
		this.messages.set([]);
		this.typing.set(false);
	}

	onKey(event: KeyboardEvent): void {
		if (event.key === 'Enter') {
			this.send(this.draft());
		}
	}

	send(text: string): void {
		const content = text.trim();
		if (!content || this.typing()) {
			return;
		}

		this.messages.update((m) => [...m, { role: 'user', content }]);
		this.draft.set('');
		this.typing.set(true);

		this.#ai.send(this.messages()).subscribe({
			next: (reply) => {
				this.messages.update((m) => [
					...m,
					{ role: 'assistant', content: reply }
				]);
				this.typing.set(false);
			},
			error: () => {
				this.typing.set(false);
				Toast.error('The assistant is unavailable right now.');
			}
		});
	}
}
